import { useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import Link from 'next/link';
import clsx from 'clsx';

import { MagnifyingGlassIcon } from '@heroicons/react/20/solid';
import {
  ArrowLeftIcon,
  ShoppingCartIcon,
  XMarkIcon,
  BuildingStorefrontIcon,
} from '@heroicons/react/24/outline';

import { Badge, Logo, StoreIcon } from '@fagmobler/ui';

import { useNavigation } from '@/contexts/navigation';
import { useCart } from '@/components/cart';
import MainNavigation from './MainNavigation';
import { useStores } from '@/components/store';
import { getStorePath } from '@/lib/omnium/getStoreUrl';

function CartButton() {
  const { setShowCart } = useNavigation();
  const { cart: { data: { orderForm: { lineItems = [] } = {} } = {} } = {} } =
    useCart();

  const orderQuantity =
    lineItems?.reduce((acc: number, item) => acc + (item.quantity || 0), 0) ??
    0;

  return (
    <button
      className={`flex items-center md:gap-2 py-2 md:px-3 md:ml-auto mr-2 focus-visible:outline-focus outline-1 outline-offset-2 relative`}
      title="handlekurv"
      onClick={() => setShowCart()}
    >
      {orderQuantity > 0 && (
        <Badge className="hidden md:block">{orderQuantity}</Badge>
      )}
      {orderQuantity > 0 && (
        <span className="block md:hidden w-2 h-2 bg-primary-50 aspect-square rounded-full absolute top-1 -right-1"></span>
      )}
      <ShoppingCartIcon className="h-5 w-5" />
    </button>
  );
}

type SearchButtonProps = {
  closeSearch(): void;
  showSearch: boolean;
  setShowSearch(b?: boolean): void;
};

function SearchButton({
  closeSearch,
  showSearch,
  setShowSearch,
}: SearchButtonProps) {
  return (
    <button
      className={clsx(
        'flex items-center justify-center gap-2',
        'md:ml-auto md:px-5 print:hidden',
        'w-8 h-8 md:w-auto md:h-auto',
        'rounded-full py-2 px-2',
        {
          'md:bg-neutral-70': !showSearch,
          'bg-neutral-0 text-neutral-80': showSearch,
        }
      )}
      title="Søk"
      onClick={() => (!showSearch ? setShowSearch() : closeSearch())}
    >
      {!showSearch && <MagnifyingGlassIcon className="h-5" />}
      <div className="hidden md:block">{!showSearch ? 'Søk' : 'Lukk søk'}</div>
      {showSearch && <XMarkIcon className="h-5 md:-mr-2" />}
    </button>
  );
}

const queryParam = 'q';

export const Header = () => {
  const router = useRouter();
  const routerEvents = router?.events;
  const { setShowSearch, showSearch, setShowStores } = useNavigation();
  const { current } = useStores();
  const store = current?.data?.store;
  const storePath = store ? getStorePath(store) : null;
  const { query } = router;

  const closeSearch = () => {
    const newQuery = Object.keys(query).reduce((acc, key) => {
      if (key !== queryParam) {
        // @ts-ignore
        acc[key] = query[key];
      }
      return acc;
    }, {});
    router.push({ query: newQuery }, undefined, {
      shallow: true,
    });
    console.log('after closing search', router.query);
    setShowSearch(false);
  };

  useEffect(() => {
    function handleRouteChange(url: string, { shallow }: any) {
      // Hide and show search on route change
      const search = url.split('?')[1];
      const params = new URLSearchParams(search);
      setShowSearch(params.has(queryParam));
    }
    if (routerEvents) {
      routerEvents.on('beforeHistoryChange', handleRouteChange);
      return () => {
        routerEvents.off('beforeHistoryChange', handleRouteChange);
      };
    }
  }, [routerEvents, setShowSearch, showSearch]);

  return (
    <>
      <div className="sticky top-0 z-40 w-full bg-neutral-80 h-full left-0 text-neutral-0 print:text-neutral-80 print:bg-[transparent] print:static">
        <div className="globalPadding xl:container grid grid-flow-col grid-cols-3 h-16 z-50 relative items-center print:block print:h-auto print:px-0 print:max-w-full">
          <div className="flex flex-grow-1 mr-auto print:hidden">
            {router && !router.pathname.includes('/utsjekk') && (
              <MainNavigation />
            )}
            {router && router.pathname === '/utsjekk' && (
              <button
                className="flex items-center rounded-full bg-neutral-70 py-3 pl-4 pr-6 cap-sm"
                onClick={router.back}
              >
                <ArrowLeftIcon className="w-4 h-4 text-neutral-0 mr-2" /> Avbryt
              </button>
            )}
            {router && router.pathname === '/utsjekk/bekreftelse' && (
              <Link legacyBehavior href="/" passHref>
                <a className="flex items-center rounded-full bg-neutral-70 py-3 pl-4 pr-6 cap-sm">
                  <ArrowLeftIcon className="w-4 h-4 text-neutral-0 mr-2" />{' '}
                  Tilbake
                </a>
              </Link>
            )}
          </div>

          <Link legacyBehavior href="/">
            <a
              className={clsx(
                'flex',
                'flex-grow-1',
                'items-center',
                'text-2xl',
                'md:text-4xl',
                'md:mx-auto',
                'print:block'
              )}
              title="Til fremsiden"
            >
              <Logo className="h-7" />
            </a>
          </Link>
          <div className="flex ml-auto print:hidden items-center">
            {store && storePath ? (
              <Link
                href={storePath}
                className="flex items-center p-2 lg:px-5 rounded-full hover:bg-neutral-90 group"
                title={`Valgt butikk: ${store.name}`}
              >
                <StoreIcon className="w-5 h-5 mr-2" />
                <span className="hidden lg:block group-hover:underline underline-offset-4">
                  {store.name?.replace('Fagmøbler ', '')}
                </span>
              </Link>
            ) : (
              <button
                onClick={() => setShowStores(true)}
                className="flex items-center"
              >
                <StoreIcon className="w-5 h-5 mr-2" />
                <span className="hidden lg:block">Velg butikk</span>
              </button>
            )}

            {router && !router.pathname.includes('/utsjekk') && (
              <>
                <CartButton />
                <SearchButton
                  closeSearch={closeSearch}
                  setShowSearch={setShowSearch}
                  showSearch={showSearch}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
